import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, LineChart, Line } from "recharts";
import api from "@/lib/api";
import StatCard from "@/components/StatCard";
import ActivityFeed from "@/components/ActivityFeed";
import SantriManager from "@/components/admin/SantriManager";
import SantriDetail from "@/components/admin/SantriDetail";
import AbsensiInput from "@/components/admin/AbsensiInput";
import PelanggaranInput from "@/components/admin/PelanggaranInput";
import RelasiWali from "@/components/admin/RelasiWali";
import Pengaturan from "@/components/admin/Pengaturan";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { greeting } from "@/components/common";
import AdminProfile from "@/components/admin/AdminProfile";
import PengasuhView from "@/components/admin/PengasuhView";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import { Users, UserCheck, CalendarCheck, ShieldAlert, Sparkles, LayoutDashboard, Link2, Settings, UserCog, Trash2, Eye, Menu, BookOpen, Building2, ClipboardCheck, ShoppingCart, Package, Wallet, Ruler, Landmark, AlertTriangle, QrCode, MoreHorizontal, Target, MessageSquare } from "lucide-react";
import EvaluasiTriwulan from "@/components/admin/EvaluasiTriwulan";
import KasirTransaksi from "@/components/admin/KasirTransaksi";
import KatalogManager from "@/components/admin/KatalogManager";
import BukuKas from "@/components/admin/BukuKas";
import RekapUkuran from "@/components/admin/RekapUkuran";
import KeuanganManager from "@/components/admin/KeuanganManager";
import PesanManager from "@/components/admin/PesanManager";
import BottomNav from "@/components/BottomNav";

const TABS = [
  { key: "overview", label: "Ringkasan", icon: LayoutDashboard, testId: "admin-tab-overview", perm: "stats.read" },
  { key: "pantau", label: "Pemantauan", icon: Eye, testId: "admin-tab-pantau", roles: ["pengasuh"] },
  { key: "santri", label: "Kelola Santri", icon: Users, testId: "admin-tab-santri", perm: "santri.read", hideRoles: ["pengasuh"] },
  { key: "ribath", label: "Keaktifan Ribath", icon: CalendarCheck, testId: "admin-tab-ribath", perm: "absensi.write" },
  { key: "departemen", label: "Keaktifan Departemen", icon: Building2, testId: "admin-tab-departemen", perm: "absensi.write" },
  { key: "ngaji", label: "Ngaji Dhuha", icon: BookOpen, testId: "admin-tab-ngaji", perm: "absensi.write", roles: ["rois_amm"] },
  { key: "pelanggaran", label: "Pelanggaran", icon: ShieldAlert, testId: "admin-tab-pelanggaran", perm: "pelanggaran.manage" },
  { key: "evaluasi", label: "Evaluasi Triwulan", icon: ClipboardCheck, testId: "admin-tab-evaluasi", perm: "evaluasi.read" },
  { key: "kasir", label: "Kasir", icon: ShoppingCart, testId: "admin-tab-kasir", perm: "kasir.transaksi" },
  { key: "katalog", label: "Katalog Produk", icon: Package, testId: "admin-tab-katalog", perm: "katalog.manage" },
  { key: "bukukas", label: "Buku Kas", icon: Wallet, testId: "admin-tab-bukukas", perm: "kas.read" },
  { key: "rekapukuran", label: "Rekap Ukuran", icon: Ruler, testId: "admin-tab-rekapukuran", perm: "katalog.manage" },
  { key: "keuangan", label: "Keuangan (RAB/LPJ)", icon: Landmark, testId: "admin-tab-keuangan", perm: "keuangan.read" },
  { key: "pesan", label: "Pesan Pembinaan", icon: MessageSquare, testId: "admin-tab-pesan", roles: ["pengasuh", "petugas_absensi"] },
  { key: "relasi", label: "Relasi Wali", icon: Link2, testId: "admin-tab-relasi", perm: "relasi.manage" },
  { key: "pengaturan", label: "Pengaturan", icon: Settings, testId: "admin-tab-pengaturan", roles: ["super_admin"] },
  { key: "profil", label: "Profil Saya", icon: UserCog, testId: "admin-tab-profil" },
];

const MONTHS_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const OV_YEAR = new Date().getFullYear();
const MONTH_CHIPS = [{ v: "all", l: "Semua" }, ...MONTHS_ID.map((m, i) => ({ v: `${OV_YEAR}-${String(i + 1).padStart(2, "0")}`, l: m }))];
const STATUS_META = [
  { key: "aktif", label: "Aktif", cls: "text-emerald-700", dot: "#3B6D11" },
  { key: "khos", label: "Khos", cls: "text-amber-700", dot: "#C08A0E" },
  { key: "boyong", label: "Boyong", cls: "text-rose-600", dot: "#e11d48" },
  { key: "diboyong", label: "Diboyong", cls: "text-slate-600", dot: "#64748b" },
];
const CAT_GRAD = { ngaji: ["#3B6D11", "#639922"], departemen: ["#2E7D5B", "#4FB58A"], ribath: ["#8A6A08", "#D4A72C"], kuliah: ["#1F5F8B", "#4A9ED8"] };
const CAT_SOLID = { ngaji: "#4E8B1E", departemen: "#2E7D5B", ribath: "#C08A0E", kuliah: "#2E77AE" };


export default function AdminDashboard() {
  const { user, hasPerm } = useAuth();
  const [detailId, setDetailId] = useState(null);
  const tabs = TABS.filter((t) => {
    if (t.hideRoles && t.hideRoles.includes(user?.role)) return false;
    if (!t.roles && !t.perm) return true;
    const byRole = t.roles ? t.roles.includes(user?.role) : false;
    const byPerm = t.perm ? hasPerm(t.perm) : false;
    return byRole || byPerm;
  });
  const preferred = user?.role === "pengasuh" ? "pantau" : "overview";
  const [tab, setTab] = useState(tabs.some((t) => t.key === preferred) ? preferred : (tabs[0]?.key || "profil"));
  const [navOpen, setNavOpen] = useState(false);
  const [santriKet, setSantriKet] = useState(null);
  const [scanIntent, setScanIntent] = useState(false);
  const [scanActive, setScanActive] = useState(false);
  const absensiKey = ["ribath", "departemen", "ngaji"].find((k) => tabs.some((t) => t.key === k));
  const canScan = (hasPerm("absensi.write") || user?.role === "rois_amm") && !!absensiKey;
  const triggerScan = () => { if (!absensiKey) return; setTab(absensiKey); setNavOpen(false); setScanIntent(true); };
  const canPesan = hasPerm("pesan.kirim") || user?.role === "petugas_absensi";
  const { data: unreadPesan = 0 } = useQuery({ queryKey: ["pesan-unread"], enabled: canPesan, refetchInterval: 30000, queryFn: async () => (await api.get("/pesan/unread-count")).data.count });
  const navPrimary = ["overview", "pantau", "santri", "evaluasi", "pelanggaran", "kasir", "keuangan", "bukukas"].map((k) => tabs.find((t) => t.key === k)).filter(Boolean);
  const bottomTabs = navPrimary.slice(0, canScan ? 3 : 4).map((t) => ({ key: t.key, label: t.label.length > 10 ? t.label.split(" ")[0] : t.label, icon: t.icon, active: tab === t.key && !navOpen, onClick: () => { setTab(t.key); setNavOpen(false); }, testId: `bottomnav-${t.key}` }));
  const bottomNavItems = [...bottomTabs, { key: "__more", label: "Lainnya", icon: MoreHorizontal, active: navOpen, onClick: () => setNavOpen(true), testId: "bottomnav-more" }];
  const bottomFab = canScan ? { icon: QrCode, label: "Scan QR", onClick: triggerScan, active: scanActive, testId: "bottomnav-scan-fab" } : undefined;

  if (detailId) {
    return <SantriDetail santriId={detailId} onBack={() => setDetailId(null)} />;
  }

  return (
    <div className="space-y-6">
      <div className="md:hidden flex items-center gap-3">
        <Sheet open={navOpen} onOpenChange={setNavOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobile-nav-toggle" aria-label="Buka menu" className="glass rounded-xl h-11 w-11 flex items-center justify-center text-emerald-900 dark:text-emerald-200 shrink-0 active:scale-95 transition-transform">
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="glass w-72 p-4 flex flex-col h-full" data-testid="mobile-nav-sheet">
            <p className="font-extrabold text-emerald-900 dark:text-emerald-100 text-lg mb-4 mt-2 shrink-0">Menu Pengurus</p>
            <div className="space-y-1 overflow-y-auto flex-1 -mr-2 pr-2">
              {tabs.map((t) => (
                <button key={t.key} data-testid={`${t.testId}-mobile`} onClick={() => { setTab(t.key); setNavOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium ${tab === t.key ? "bg-emerald-700 text-white shadow" : "text-slate-700 dark:text-slate-200 hover:bg-emerald-50 dark:hover:bg-white/5"}`}>
                  <t.icon className="h-4 w-4" /> {t.label}{t.key === "pesan" && unreadPesan > 0 && <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="pesan-unread-badge">{unreadPesan}</span>}
                </button>
              ))}
            </div>
          </SheetContent>
        </Sheet>
        <span className="font-semibold text-emerald-900 dark:text-emerald-100 dark:text-emerald-100 flex items-center gap-2 min-w-0 truncate">
          {(() => { const T = tabs.find((t) => t.key === tab); return T ? <><T.icon className="h-4 w-4 shrink-0" /> <span className="truncate">{T.label}</span></> : "Menu"; })()}
        </span>
      </div>
      <div className="glass rounded-2xl p-2 gap-1 overflow-x-auto hidden md:flex">
        {tabs.map((t) => (
          <button key={t.key} data-testid={t.testId} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${tab === t.key ? "bg-emerald-700 text-white shadow-sm" : "text-slate-600 dark:text-slate-300 hover:bg-emerald-50 dark:hover:bg-white/5"}`}>
            <t.icon className="h-4 w-4" /> {t.label}{t.key === "pesan" && unreadPesan > 0 && <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="pesan-unread-badge">{unreadPesan}</span>}
          </button>
        ))}
      </div>

      <motion.div key={tab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        {tab === "overview" && <Overview goTo={setTab} onStatus={setSantriKet} />}
        {tab === "pantau" && <PengasuhView onOpenDetail={setDetailId} />}
        {tab === "santri" && <SantriManager onOpenDetail={setDetailId} canManage={hasPerm("santri.manage")} canDelete={hasPerm("santri.delete")} canImport={hasPerm("import.santri")} initialKet={santriKet} />}
        {tab === "ribath" && <AbsensiInput lockKategori="keaktifan_ribath" autoScan={absensiKey === "ribath" && scanIntent} onAutoScanDone={() => setScanIntent(false)} onScanStateChange={setScanActive} />}
        {tab === "departemen" && <AbsensiInput lockKategori="keaktifan_departemen" autoScan={absensiKey === "departemen" && scanIntent} onAutoScanDone={() => setScanIntent(false)} onScanStateChange={setScanActive} />}
        {tab === "ngaji" && <AbsensiInput lockKategori="ngaji_dhuha" autoScan={absensiKey === "ngaji" && scanIntent} onAutoScanDone={() => setScanIntent(false)} onScanStateChange={setScanActive} />}
        {tab === "pelanggaran" && <PelanggaranInput />}
        {tab === "evaluasi" && <EvaluasiTriwulan />}
        {tab === "kasir" && <KasirTransaksi />}
        {tab === "katalog" && <KatalogManager />}
        {tab === "bukukas" && <BukuKas />}
        {tab === "rekapukuran" && <RekapUkuran />}
        {tab === "keuangan" && <KeuanganManager />}
        {tab === "pesan" && <PesanManager />}
        {tab === "relasi" && <RelasiWali />}
        {tab === "pengaturan" && <Pengaturan />}
        {tab === "profil" && <AdminProfile />}
      </motion.div>

      <BottomNav items={bottomNavItems} fab={bottomFab} />
    </div>
  );
}

function Overview({ goTo, onStatus }) {
  const qc = useQueryClient();
  const { hasPerm, user } = useAuth();
  const canManageAct = hasPerm("activities.manage");
  const canManage = hasPerm("santri.manage");
  const isSuper = user?.role === "super_admin";
  const [bulan, setBulan] = useState("all");
  const [threshold, setThreshold] = useState(75);
  const { data: ov } = useQuery({ queryKey: ["overview", bulan], queryFn: async () => (await api.get("/overview", { params: { bulan } })).data });
  const { data: activities = [] } = useQuery({ queryKey: ["activities"], queryFn: async () => (await api.get("/activities")).data, refetchInterval: 8000 });
  const { data: conn } = useQuery({ queryKey: ["gs-conn"], queryFn: async () => (await api.get("/sheets/connection")).data, enabled: isSuper, refetchInterval: 60000 });

  const seedMut = useMutation({ mutationFn: async () => api.post("/seed/demo"), onSuccess: (r) => { toast.success(r.data.message || "Data contoh dibuat"); qc.invalidateQueries(); } });
  const delAct = useMutation({ mutationFn: async (id) => api.delete(`/activities/${id}`), onSuccess: () => { toast.success("Aktivitas & datanya dihapus"); qc.invalidateQueries(); }, onError: (e) => toast.error(e?.response?.data?.detail || "Gagal menghapus") });
  const clearAct = useMutation({ mutationFn: async () => api.post("/activities/clear"), onSuccess: (r) => { toast.success(`${r.data.deleted} aktivitas dibersihkan`); qc.invalidateQueries({ queryKey: ["activities"] }); }, onError: (e) => toast.error(e?.response?.data?.detail || "Gagal") });

  const monthLabel = bulan === "all" ? "Rata-rata (s.d. bulan berjalan)" : `${MONTHS_ID[+bulan.slice(5, 7) - 1]} ${bulan.slice(0, 4)}`;
  const totalSantri = ov ? Object.values(ov.status_counts).reduce((a, b) => a + b, 0) : 0;
  const scores = ov?.keaktifan_scores || [];
  const totalScored = scores.length;
  const meeting = scores.filter((s) => s >= threshold).length;
  const belowCount = totalScored - meeting;
  const pctMeeting = totalScored ? Math.round((meeting / totalScored) * 100) : 0;
  const canClickStatus = canManage || user?.role === "pengasuh";
  const clickStatus = (key) => {
    if (canManage) { onStatus?.(key); goTo("santri"); }
    else if (user?.role === "pengasuh") goTo("pantau");
  };

  return (
    <div className="space-y-6">
      {ov?.scope && <div className="rounded-xl bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-900/50 px-4 py-2.5 text-sm text-indigo-800 dark:text-indigo-200 flex items-center gap-2" data-testid="scope-banner"><Building2 className="h-4 w-4 shrink-0" /> Data ditampilkan khusus unit tugas Anda: <strong className="capitalize">{ov.scope.tipe} {ov.scope.nama}</strong></div>}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-emerald-900 dark:text-emerald-100 tracking-tight">{greeting()}, {user?.name?.split(" ")[0] || "Pengurus"}</h2>
          <p className="text-slate-500 dark:text-slate-300 dark:text-slate-400 dark:text-slate-500 dark:text-slate-300 text-sm">Ringkasan keaktifan pondok · <span className="font-medium text-emerald-800 dark:text-emerald-300">{monthLabel}</span></p>
        </div>
        {isSuper && conn && (
          <span data-testid="gs-connection-status" className={`text-xs px-3 py-1.5 rounded-full font-medium flex items-center gap-1.5 ${conn.ok ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-700"}`}>
            <span className={`h-2 w-2 rounded-full ${conn.ok ? "bg-emerald-500" : "bg-rose-500"}`} />
            {conn.ok ? `Google Sheet tersambung${conn.email ? " · " + conn.email : ""}` : conn.reason === "not_connected" ? "Google belum terhubung" : "Koneksi Google kedaluwarsa — hubungkan ulang di Pengaturan"}
          </span>
        )}
      </div>

      {isSuper && conn && !conn.ok && (
        <div data-testid="gs-warning-banner" className="rounded-2xl border border-amber-300 bg-amber-50 p-4 flex flex-col sm:flex-row items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-amber-900">{conn.reason === "not_connected" ? "Google Sheet belum terhubung" : "Koneksi Google Sheet terputus / kedaluwarsa"}</p>
            <p className="text-sm text-amber-800 mt-0.5">Impor & ekspor backup otomatis ke Google Sheet tidak berjalan. Hubungkan ulang akun Google di <b>Pengaturan → Sinkronisasi &amp; Backup</b>. Untuk mencegah kedaluwarsa berulang, ubah status OAuth App di Google Cloud Console dari <b>“Testing”</b> ke <b>“In production”</b>.</p>
          </div>
          <Button size="sm" data-testid="gs-warning-reconnect" onClick={() => goTo("pengaturan")} className="bg-amber-600 hover:bg-amber-700 shrink-0 rounded-xl">Buka Pengaturan</Button>
        </div>
      )}

      {/* Month chips */}
      <div className="glass rounded-2xl p-2 flex gap-1.5 overflow-x-auto" data-testid="overview-month-chips">
        {MONTH_CHIPS.map((c) => (
          <button key={c.v} data-testid={`overview-month-${c.v}`} onClick={() => setBulan(c.v)}
            className={`px-3.5 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${bulan === c.v ? "bg-emerald-700 text-white shadow-sm" : "text-slate-600 dark:text-slate-300 hover:bg-emerald-50 dark:hover:bg-white/5"}`}>{c.l}</button>
        ))}
      </div>

      {totalSantri === 0 && isSuper && (
        <div className="glass rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3"><Sparkles className="h-6 w-6 text-emerald-600" /><div><p className="font-semibold text-emerald-950 dark:text-emerald-50">Mulai dengan data contoh</p><p className="text-sm text-slate-500 dark:text-slate-300">Muat 5 santri & aktivitas contoh untuk mencoba sistem.</p></div></div>
          <Button data-testid="seed-demo-button" onClick={() => seedMut.mutate()} disabled={seedMut.isPending} className="gap-2 rounded-xl bg-emerald-700 hover:bg-emerald-800"><Sparkles className="h-4 w-4" /> Muat Data Contoh</Button>
        </div>
      )}

      {/* Status keanggotaan */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4" data-testid="overview-status-cards">
        {STATUS_META.map((st, i) => (
          <motion.button key={st.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            onClick={() => clickStatus(st.key)} disabled={!canClickStatus}
            className={`glass rounded-2xl p-5 text-left ${canClickStatus ? "glass-hover cursor-pointer" : "cursor-default"}`} data-testid={`overview-status-${st.key}`}>
            <div className="flex items-center gap-2 mb-1"><span className="h-2.5 w-2.5 rounded-full" style={{ background: st.dot }} /><p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">{st.label}</p></div>
            <p className={`text-3xl font-extrabold ${st.cls}`}>{ov?.status_counts?.[st.key] ?? "—"}</p>
            <p className="text-[11px] text-slate-400 dark:text-slate-500 dark:text-slate-300 mt-0.5">santri</p>
          </motion.button>
        ))}
      </div>

      {/* Tabel persentase kategori */}
      <div className="glass rounded-2xl p-5 overflow-x-auto" data-testid="overview-kategori-table">
        <h3 className="font-semibold text-emerald-900 dark:text-emerald-100 mb-3">Rekap Persentase Keaktifan — {monthLabel}</h3>
        <table className="w-full text-sm min-w-[520px]">
          <thead>
            <tr className="text-left text-slate-500 dark:text-slate-300 border-b border-emerald-100 dark:border-white/10">
              {(ov?.kategori || []).map((k) => <th key={k.key} className="py-2 font-semibold"><span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: CAT_SOLID[k.key] }} />{k.label}</span></th>)}
              <th className="py-2 font-bold text-emerald-800 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              {(ov?.kategori || []).map((k) => <td key={k.key} className="py-3 font-bold text-emerald-950 dark:text-emerald-50 text-lg">{k.persen == null ? "–" : `${k.persen}%`}</td>)}
              <td className="py-3 text-right"><span className="inline-block px-3 py-1.5 rounded-xl bg-emerald-700 text-white font-extrabold text-lg" data-testid="overview-total">{ov?.total == null ? "–" : `${ov.total}%`}</span></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Widget ambang kehadiran (dapat disetel) */}
      <div className="glass rounded-2xl p-5" data-testid="threshold-widget">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            <h3 className="font-semibold text-emerald-900 dark:text-emerald-100">Santri Memenuhi Ambang Kehadiran</h3>
          </div>
          <span className="text-sm font-extrabold px-3 py-1 rounded-full bg-emerald-700 text-white" data-testid="threshold-value">≥ {threshold}%</span>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-300 mt-0.5">Rata-rata keaktifan santri aktif · {monthLabel}</p>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="rounded-2xl p-4 bg-gradient-to-br from-emerald-600 to-lime-500 text-white" data-testid="threshold-meeting">
            <p className="text-3xl font-extrabold">{meeting}</p>
            <p className="text-xs font-medium opacity-90 mt-0.5">Memenuhi (≥ {threshold}%)</p>
          </div>
          <div className="rounded-2xl p-4 bg-white/60 dark:bg-white/5 border border-emerald-100 dark:border-white/10" data-testid="threshold-below">
            <p className="text-3xl font-extrabold text-rose-600 dark:text-rose-400">{belowCount}</p>
            <p className="text-xs font-medium text-slate-500 dark:text-slate-300 mt-0.5">Di bawah ambang</p>
          </div>
          <div className="rounded-2xl p-4 bg-white/60 dark:bg-white/5 border border-emerald-100 dark:border-white/10" data-testid="threshold-percent">
            <p className="text-3xl font-extrabold text-emerald-800 dark:text-emerald-300">{pctMeeting}%</p>
            <p className="text-xs font-medium text-slate-500 dark:text-slate-300 mt-0.5">dari {totalScored} santri terukur</p>
          </div>
        </div>
        <div className="mt-5">
          <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-300 mb-2">
            <span>Geser untuk menyetel ambang</span>
            <span className="font-semibold text-emerald-800 dark:text-emerald-300">{threshold}%</span>
          </div>
          <Slider data-testid="threshold-slider" value={[threshold]} min={50} max={100} step={1} disabled={!isSuper} onValueChange={(v) => isSuper && setThreshold(v[0])} />
          <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>50%</span><span>75%</span><span>100%</span></div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <DistBar title="Jumlah Santri per Departemen" data={ov?.per_departemen || []} grad={CAT_GRAD.departemen} testId="overview-dist-departemen" />
        <DistBar title="Jumlah Santri per Ribath" data={ov?.per_ribath || []} grad={CAT_GRAD.ribath} testId="overview-dist-ribath" />
      </div>

      {/* Tren bulanan */}
      <div className="glass rounded-2xl p-5" data-testid="overview-tren">
        <h3 className="font-semibold text-emerald-900 dark:text-emerald-100 mb-4">Tren Rata-rata Keaktifan Bulanan (%)</h3>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={ov?.tren || []} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#D1FAE5" />
            <XAxis dataKey="bulan" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} domain={[0, 100]} />
            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #A7F3D0" }} formatter={(v) => [`${v}%`, "Rata-rata"]} />
            <Line type="monotone" dataKey="persen" stroke="#059669" strokeWidth={3} dot={{ r: 3, fill: "#059669" }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Aktivitas terkini — hanya tampil jika ada */}
      {activities.length > 0 && (
        <div>
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" /></span>
              <h3 className="font-semibold text-emerald-900 dark:text-emerald-100">Aktivitas Terkini</h3>
            </div>
            {canManageAct && (
              <Button variant="outline" size="sm" className="gap-2 rounded-xl bg-white/60 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="clear-activities-button"
                onClick={() => { if (window.confirm("Bersihkan SEMUA daftar aktivitas terkini? Data absensi & pelanggaran tetap aman.")) clearAct.mutate(); }}>
                <Trash2 className="h-4 w-4" /> Hapus Semua
              </Button>
            )}
          </div>
          <ActivityFeed activities={activities} onDelete={canManageAct ? (a) => { if (window.confirm(`Hapus aktivitas "${a.judul}" untuk ${a.santri_nama}? Data absensi/pelanggaran terkait ikut terhapus.`)) delAct.mutate(a.id); } : undefined} />
        </div>
      )}
    </div>
  );
}

function DistBar({ title, data, grad, testId }) {
  const gid = `grad-${testId}`;
  return (
    <div className="glass rounded-2xl p-5" data-testid={testId}>
      <h3 className="font-semibold text-emerald-900 dark:text-emerald-100 mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} margin={{ top: 16, right: 18, left: -22, bottom: 0 }}>
          <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={grad[1]} /><stop offset="100%" stopColor={grad[0]} /></linearGradient></defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#D1FAE5" />
          <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} interval={0} angle={-20} textAnchor="end" height={50} />
          <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip cursor={{ fill: "#ECFDF5" }} contentStyle={{ borderRadius: 12, border: "1px solid #A7F3D0" }} formatter={(v) => [v, "Santri"]} />
          <Bar dataKey="jumlah" name="Santri" shape={(p) => <Bar3D {...p} gid={gid} dark={grad[0]} />} maxBarSize={48} animationDuration={800} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// Pseudo-3D bar: flat accurate top (front face top = true value) + right side face + top cap + soft shadow.
export function Bar3D({ x, y, width, height, gid, dark }) {
  if (!height || height <= 0 || width <= 0) return null;
  const d = Math.max(4, Math.min(9, width * 0.22));
  const r = Math.min(6, width / 2);
  return (
    <g>
      <ellipse cx={x + width / 2} cy={y + height} rx={width * 0.55} ry={3} fill="#0f172a" opacity="0.10" />
      <path d={`M ${x + r} ${y} L ${x + r + d} ${y - d} L ${x + width + d} ${y - d} L ${x + width} ${y} Z`} fill={dark} opacity="0.5" />
      <path d={`M ${x + width} ${y} L ${x + width + d} ${y - d} L ${x + width + d} ${y + height - d} L ${x + width} ${y + height} Z`} fill={dark} opacity="0.85" />
      <path d={`M ${x} ${y + r} Q ${x} ${y} ${x + r} ${y} L ${x + width - r} ${y} Q ${x + width} ${y} ${x + width} ${y + r} L ${x + width} ${y + height} L ${x} ${y + height} Z`} fill={`url(#${gid})`} />
    </g>
  );
}
